import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
    ArrowLeft, 
    Calendar, 
    ChevronLeft, 
    ChevronRight,
    DollarSign,
    FileText,
    CheckCircle,
    Clock,
    AlertTriangle,
    XCircle,
    CircleDot,
    Building2,
    ChevronsUpDown,
    Check,
    Search
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '../components/ui/popover';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '../components/ui/command';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Month names in Spanish
const MONTHS = [
    'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
    'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'
];

const MONTHS_FULL = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

// Estado colors and icons
const getEstadoConfig = (estado) => {
    const configs = {
        'servicio_en_curso': { 
            color: 'bg-blue-500', 
            bgLight: 'bg-blue-50 border-blue-200',
            textColor: 'text-blue-700',
            icon: CircleDot,
            label: 'En Curso'
        },
        'servicio_terminado': { 
            color: 'bg-slate-500', 
            bgLight: 'bg-slate-50 border-slate-200',
            textColor: 'text-slate-700',
            icon: CheckCircle,
            label: 'Servicio Terminado'
        },
        'cobranza_activa': { 
            color: 'bg-amber-500', 
            bgLight: 'bg-amber-50 border-amber-200',
            textColor: 'text-amber-700',
            icon: Clock,
            label: 'Cobranza Activa'
        },
        'facturado': { 
            color: 'bg-indigo-500', 
            bgLight: 'bg-indigo-50 border-indigo-200',
            textColor: 'text-indigo-700',
            icon: FileText,
            label: 'Facturado'
        },
        'pagado': { 
            color: 'bg-emerald-500', 
            bgLight: 'bg-emerald-50 border-emerald-200',
            textColor: 'text-emerald-700',
            icon: CheckCircle,
            label: 'Pagado'
        },
        'pendiente_pago': { 
            color: 'bg-yellow-500', 
            bgLight: 'bg-yellow-50 border-yellow-200',
            textColor: 'text-yellow-700',
            icon: Clock,
            label: 'Pendiente Pago'
        },
        'pago_parcial': { 
            color: 'bg-lime-500', 
            bgLight: 'bg-lime-50 border-lime-200',
            textColor: 'text-lime-700',
            icon: DollarSign,
            label: 'Pago Parcial'
        },
        'vencido': { 
            color: 'bg-orange-500', 
            bgLight: 'bg-orange-50 border-orange-200',
            textColor: 'text-orange-700',
            icon: AlertTriangle,
            label: 'Vencido'
        },
        'en_mora': { 
            color: 'bg-red-500', 
            bgLight: 'bg-red-50 border-red-200',
            textColor: 'text-red-700',
            icon: AlertTriangle,
            label: 'En Mora'
        },
        'compromiso_pago': { 
            color: 'bg-orange-500', 
            bgLight: 'bg-orange-50 border-orange-200',
            textColor: 'text-orange-700',
            icon: Clock,
            label: 'Compromiso Pago'
        },
        'castigado': { 
            color: 'bg-slate-700', 
            bgLight: 'bg-slate-100 border-slate-300',
            textColor: 'text-slate-800',
            icon: XCircle,
            label: 'Castigado'
        },
        'edp_enviado': { 
            color: 'bg-purple-500', 
            bgLight: 'bg-purple-50 border-purple-200',
            textColor: 'text-purple-700',
            icon: FileText,
            label: 'EDP Enviado'
        },
        'edp_aprobado': { 
            color: 'bg-teal-500', 
            bgLight: 'bg-teal-50 border-teal-200',
            textColor: 'text-teal-700',
            icon: CheckCircle,
            label: 'EDP Aprobado'
        },
        'espera_oc': { 
            color: 'bg-cyan-500', 
            bgLight: 'bg-cyan-50 border-cyan-200',
            textColor: 'text-cyan-700',
            icon: Clock,
            label: 'Espera OC'
        },
        'servicio_interrumpido': { 
            color: 'bg-red-700', 
            bgLight: 'bg-red-100 border-red-300',
            textColor: 'text-red-800',
            icon: XCircle,
            label: 'Interrumpido'
        },
    };
    return configs[estado] || { 
        color: 'bg-gray-400', 
        bgLight: 'bg-gray-50 border-gray-200',
        textColor: 'text-gray-700',
        icon: CircleDot,
        label: estado || 'Sin Estado'
    };
};

// EDP Estado config
const getEdpEstadoConfig = (estado) => {
    const configs = {
        'sin_edp': { color: 'bg-gray-400', label: 'Sin EDP', textColor: 'text-gray-600' },
        'enviado': { color: 'bg-[#5B9A9A]', label: 'Enviado', textColor: 'text-[#5B9A9A]' },
        'aprobado': { color: 'bg-emerald-500', label: 'Aprobado', textColor: 'text-emerald-600' },
        'rechazado': { color: 'bg-red-500', label: 'Rechazado', textColor: 'text-red-600' },
    };
    return configs[estado] || { color: 'bg-gray-400', label: estado || '-', textColor: 'text-gray-600' };
};

// Format currency
const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '-';
    return new Intl.NumberFormat('es-CL', {
        style: 'currency',
        currency: 'CLP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(amount);
};

const ServicioTimeline = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [servicio, setServicio] = useState(null);
    const [serviciosPeriodo, setServiciosPeriodo] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [availableYears, setAvailableYears] = useState([]);
    const [edps, setEdps] = useState({});
    const [isInitialized, setIsInitialized] = useState(false);
    // Tracks the last year already loaded by the initial bootstrap or the
    // selectedYear effect, so we never refetch the same year twice.
    const lastLoadedYearRef = useRef(null);

    // Service selector (combobox with search)
    const [allServiciosOpts, setAllServiciosOpts] = useState([]); // list of unique service options across empresa
    const [selectorOpen, setSelectorOpen] = useState(false);
    const [loadingSelector, setLoadingSelector] = useState(false);

    useEffect(() => {
        fetchAllServiciosForSelector();
    }, []);

    /**
     * Fetch services flagged to appear in the timeline selector.
     * Only services with `mostrar_en_timeline = true` are included.
     * For each recurring group we keep ONE entry (most recent period as anchor).
     */
    const fetchAllServiciosForSelector = async () => {
        try {
            setLoadingSelector(true);
            const all = [];
            let page = 1;
            const pageSize = 500;
            for (let i = 0; i < 10; i++) {
                const res = await axios.get(`${API_URL}/api/servicios`, {
                    params: { page, page_size: pageSize, mostrar_en_timeline: true },
                });
                const items = res.data.items || res.data || [];
                if (items.length === 0) break;
                all.push(...items);
                if (items.length < pageSize) break;
                page += 1;
            }

            // Backend already filters mostrar_en_timeline=true
            const visibles = all;

            // Deduplicate by grupo_recurrente_id (one entry per recurring group)
            const byKey = new Map();
            for (const s of visibles) {
                const key = s.grupo_recurrente_id
                    ? `g:${s.grupo_recurrente_id}`
                    : `i:${s.id}`;
                const existing = byKey.get(key);
                if (!existing) {
                    byKey.set(key, s);
                } else {
                    const newest =
                        (s.periodo_anio || 0) * 100 + (s.periodo_mes || 0) >
                        (existing.periodo_anio || 0) * 100 + (existing.periodo_mes || 0)
                            ? s
                            : existing;
                    byKey.set(key, newest);
                }
            }

            const opts = Array.from(byKey.values())
                .map(s => ({
                    id: s.id,
                    cliente_nombre: s.cliente_nombre || '',
                    nombre_producto: s.nombre_producto || s.descripcion || '',
                    faena: s.faena || '',
                    label: `${s.cliente_nombre || 'Cliente'} — ${s.nombre_producto || s.descripcion || 'Servicio'}${s.faena ? ` · ${s.faena}` : ''}`,
                    searchText: `${s.cliente_nombre || ''} ${s.nombre_producto || ''} ${s.descripcion || ''} ${s.faena || ''}`.toLowerCase(),
                }))
                .sort((a, b) => a.label.localeCompare(b.label, 'es'));

            setAllServiciosOpts(opts);
        } catch (e) {
            console.error('Error loading services for selector:', e);
        } finally {
            setLoadingSelector(false);
        }
    };

    const handleSelectServicio = (servicioId) => {
        if (!servicioId || servicioId === id) {
            setSelectorOpen(false);
            return;
        }
        setSelectorOpen(false);
        navigate(`/servicios/${servicioId}/timeline`);
    };

    useEffect(() => {
        fetchServicioData();
    }, [id]);

    useEffect(() => {
        if (servicio && isInitialized && lastLoadedYearRef.current !== selectedYear) {
            lastLoadedYearRef.current = selectedYear;
            fetchServiciosPorAnio(selectedYear);
        }
    }, [selectedYear, isInitialized]);

    const fetchServicioData = async () => {
        try {
            setLoading(true);
            // 1) Main service detail.
            const response = await axios.get(`${API_URL}/api/servicios/${id}`);
            setServicio(response.data);

            // 2) Year discovery & per-year timeline fetch run IN PARALLEL.
            // Year discovery needs only one page (most groups span <100 rows).
            // We pre-select the current year (or the service's own year) before
            // discovery completes so the timeline grid can render immediately.
            const currentYear = new Date().getFullYear();
            const initialYear = currentYear;
            setSelectedYear(initialYear);
            lastLoadedYearRef.current = initialYear;

            const yearsPromise = axios
                .get(`${API_URL}/api/servicios`, {
                    params: { cliente_id: response.data.cliente_id, page: 1, page_size: 100 },
                })
                .then(res => res.data.items || res.data || [])
                .catch(() => []);

            const [allItems] = await Promise.all([
                yearsPromise,
                fetchServiciosPorAnioInternal(response.data, initialYear),
            ]);

            // Filter by same recurrence group (preferred) or by product name when
            // the service is not part of a group.
            const hasGroupForYears = !!response.data.grupo_recurrente_id;
            const relatedServices = allItems.filter(s => {
                if (hasGroupForYears) {
                    return s.grupo_recurrente_id === response.data.grupo_recurrente_id
                        || s.id === response.data.id;
                }
                if (s.nombre_producto === response.data.nombre_producto) return true;
                if (s.nombre_producto && response.data.nombre_producto &&
                    s.nombre_producto.toLowerCase().trim() === response.data.nombre_producto.toLowerCase().trim()) {
                    return true;
                }
                return false;
            });

            const yearsSet = new Set();
            relatedServices.forEach(s => { if (s.periodo_anio) yearsSet.add(s.periodo_anio); });
            yearsSet.add(currentYear);
            yearsSet.add(currentYear + 1);
            yearsSet.add(currentYear - 1);
            if (response.data.periodo_anio) yearsSet.add(response.data.periodo_anio);

            const years = Array.from(yearsSet).sort((a, b) => a - b);
            setAvailableYears(years);
            setIsInitialized(true);
        } catch (error) {
            console.error('Error fetching servicio:', error);
            toast.error('Error al cargar el servicio');
        } finally {
            setLoading(false);
        }
    };
    
    // Internal function that takes servicio as parameter to avoid closure issues
    const fetchServiciosPorAnioInternal = async (servicioData, year) => {
        try {
            const response = await axios.get(`${API_URL}/api/servicios`, {
                params: {
                    cliente_id: servicioData.cliente_id,
                    periodo_anio: year,
                    page_size: 100
                }
            });
            
            const items = response.data.items || response.data || [];

            // Prefer strict matching: if the current servicio belongs to a recurrence
            // group, only services in that exact group are part of the timeline.
            // This avoids orphan duplicates (e.g. legacy rows with the same product
            // name but no grupo_recurrente_id) being shown for the same month.
            const hasGroup = !!servicioData.grupo_recurrente_id;
            const relacionados = items.filter(s => {
                if (hasGroup) {
                    return s.grupo_recurrente_id === servicioData.grupo_recurrente_id
                        || s.id === servicioData.id;
                }
                // Non-recurring service: fall back to product-name match.
                if (s.nombre_producto === servicioData.nombre_producto) return true;
                if (s.nombre_producto && servicioData.nombre_producto &&
                    s.nombre_producto.toLowerCase().trim() === servicioData.nombre_producto.toLowerCase().trim()) {
                    return true;
                }
                return false;
            });
            
            setServiciosPeriodo(relacionados);

            // Single batch request for all EDPs of the visible services.
            // Replaces N parallel HTTP roundtrips with 1.
            const edpMap = {};
            const idsRequiringEdp = relacionados.filter(s => s.requiere_edp).map(s => s.id);
            if (idsRequiringEdp.length > 0) {
                try {
                    const res = await axios.get(`${API_URL}/api/edps`, {
                        params: { servicio_ids: idsRequiringEdp.join(',') },
                    });
                    const list = res.data || [];
                    // Pick the most recent EDP per service (list is desc by numero_edp).
                    for (const edp of list) {
                        if (!edpMap[edp.servicio_id]) edpMap[edp.servicio_id] = edp;
                    }
                } catch (e) {
                    /* ignore — services without EDPs simply remain unmapped */
                }
            }
            setEdps(edpMap);
        } catch (error) {
            console.error('Error fetching servicios por año:', error);
        }
    };

    const fetchServiciosPorAnio = async (year) => {
        if (!servicio) return;
        await fetchServiciosPorAnioInternal(servicio, year);
    };

    const getServiceForMonth = (month) => {
        // serviciosPeriodo already contains only services for selectedYear.
        // If multiple rows exist for the same month (legacy duplicates), prefer:
        //   1) the service the user is currently viewing,
        //   2) one with the same recurrence group as the current servicio,
        //   3) any with a grupo_recurrente_id (canonical recurring row),
        //   4) otherwise the first match.
        const matches = serviciosPeriodo.filter(s => s.periodo_mes === month);
        if (matches.length === 0) return null;
        if (matches.length === 1) return matches[0];
        const groupId = servicio?.grupo_recurrente_id;
        return (
            matches.find(s => s.id === servicio?.id) ||
            (groupId && matches.find(s => s.grupo_recurrente_id === groupId)) ||
            matches.find(s => !!s.grupo_recurrente_id) ||
            matches[0]
        );
    };

    const handleYearChange = (direction) => {
        const newYear = selectedYear + direction;
        if (availableYears.includes(newYear)) {
            setSelectedYear(newYear);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5B9A9A]"></div>
            </div>
        );
    }

    if (!servicio) {
        return (
            <div className="p-6">
                <p className="text-slate-500">Servicio no encontrado</p>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-6 space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                <div className="flex items-start gap-4 flex-1 min-w-0">
                    <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => navigate(`/servicios/${id}`)}
                    >
                        <ArrowLeft size={20} />
                    </Button>
                    <div className="flex-1 min-w-0">
                        <h1 className="text-2xl font-bold text-slate-900">
                            Línea de Tiempo
                        </h1>
                        <div className="flex flex-col md:flex-row md:items-center md:gap-3 mt-1">
                            <p className="text-slate-500 flex items-center gap-2">
                                <Building2 size={14} className="shrink-0" />
                                <span className="truncate">
                                    {servicio.cliente_nombre || 'Cliente'} - {servicio.nombre_producto || servicio.descripcion}
                                    {servicio.faena ? (
                                        <span className="text-slate-400"> · {servicio.faena}</span>
                                    ) : null}
                                </span>
                            </p>

                            {/* Service Selector (Combobox with search) — al lado del servicio */}
                            <div className="md:w-72 mt-2 md:mt-0">
                                <Popover open={selectorOpen} onOpenChange={setSelectorOpen}>
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="outline"
                                            role="combobox"
                                            aria-expanded={selectorOpen}
                                            className="w-full justify-between text-left h-8 text-xs font-normal"
                                        >
                                            <span className="truncate flex items-center gap-2">
                                                <Search size={12} className="text-slate-400 shrink-0" />
                                                {loadingSelector
                                                    ? 'Cargando servicios...'
                                                    : 'Cambiar servicio…'}
                                            </span>
                                            <ChevronsUpDown size={12} className="ml-2 opacity-50 shrink-0" />
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-[min(90vw,520px)] p-0" align="start">
                                        <Command
                                            filter={(value, search) => {
                                                return value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
                                            }}
                                        >
                                            <CommandInput placeholder="Buscar por cliente, servicio o faena…" />
                                            <CommandList>
                                                <CommandEmpty>
                                                    <div className="px-2 py-4 text-center text-sm">
                                                        <p className="text-slate-600 mb-1">No hay servicios marcados</p>
                                                        <p className="text-xs text-slate-400">
                                                            Marca el checkbox <em>"Mostrar en línea de tiempo"</em> en la tarjeta del servicio que quieras ver aquí.
                                                        </p>
                                                    </div>
                                                </CommandEmpty>
                                                <CommandGroup>
                                                    {allServiciosOpts.map(opt => (
                                                        <CommandItem
                                                            key={opt.id}
                                                            value={`${opt.searchText} ${opt.id}`}
                                                            onSelect={() => handleSelectServicio(opt.id)}
                                                            className="cursor-pointer"
                                                        >
                                                            <Check
                                                                size={14}
                                                                className={cn(
                                                                    "mr-2 shrink-0",
                                                                    opt.id === id ? "opacity-100 text-teal-600" : "opacity-0"
                                                                )}
                                                            />
                                                            <div className="flex-1 min-w-0">
                                                                <p className="text-sm font-medium text-slate-900 truncate">
                                                                    {opt.cliente_nombre || 'Cliente'}
                                                                </p>
                                                                <p className="text-xs text-slate-500 truncate">
                                                                    {opt.nombre_producto || 'Servicio'}
                                                                    {opt.faena ? ` · ${opt.faena}` : ''}
                                                                </p>
                                                            </div>
                                                        </CommandItem>
                                                    ))}
                                                </CommandGroup>
                                            </CommandList>
                                        </Command>
                                    </PopoverContent>
                                </Popover>
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* Year Selector */}
                <div className="flex items-center gap-2 shrink-0">
                    <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleYearChange(-1)}
                        disabled={!availableYears.includes(selectedYear - 1)}
                    >
                        <ChevronLeft size={18} />
                    </Button>
                    <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                        <SelectTrigger className="w-32">
                            <Calendar size={14} className="mr-2" />
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {availableYears.map(year => (
                                <SelectItem key={year} value={year.toString()}>
                                    {year}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleYearChange(1)}
                        disabled={!availableYears.includes(selectedYear + 1)}
                    >
                        <ChevronRight size={18} />
                    </Button>
                </div>
            </div>

            {/* Timeline Legend */}
            <Card>
                <CardHeader className="py-3">
                    <CardTitle className="text-sm font-medium text-slate-600">Leyenda de Estados</CardTitle>
                </CardHeader>
                <CardContent className="py-2">
                    <div className="flex flex-wrap gap-3">
                        {['servicio_en_curso', 'facturado', 'pagado', 'en_mora', 'cobranza_activa', 'castigado'].map(estado => {
                            const config = getEstadoConfig(estado);
                            return (
                                <div key={estado} className="flex items-center gap-1.5">
                                    <div className={`w-3 h-3 rounded-full ${config.color}`}></div>
                                    <span className="text-xs text-slate-600">{config.label}</span>
                                </div>
                            );
                        })}
                    </div>
                </CardContent>
            </Card>

            {/* Timeline Grid */}
            <Card>
                <CardContent className="p-6">
                    <div className="grid grid-cols-12 gap-2 md:gap-3">
                        {MONTHS.map((month, index) => {
                            const monthNum = index + 1;
                            const svc = getServiceForMonth(monthNum);
                            const estadoConfig = svc ? getEstadoConfig(svc.estado) : null;
                            const edp = svc ? edps[svc.id] : null;
                            const edpConfig = edp ? getEdpEstadoConfig(edp.estado) : null;
                            const isCurrentMonth = new Date().getMonth() === index && new Date().getFullYear() === selectedYear;
                            const isFuture = selectedYear > new Date().getFullYear() || 
                                            (selectedYear === new Date().getFullYear() && monthNum > new Date().getMonth() + 1);
                            
                            return (
                                <div 
                                    key={monthNum}
                                    className={`relative flex flex-col rounded-lg border-2 transition-all min-h-[180px] ${
                                        svc 
                                            ? `${estadoConfig.bgLight} hover:shadow-md cursor-pointer` 
                                            : isFuture 
                                                ? 'bg-slate-50 border-dashed border-slate-200' 
                                                : 'bg-white border-slate-100'
                                    } ${isCurrentMonth ? 'ring-2 ring-blue-400 ring-offset-2' : ''}`}
                                    onClick={() => svc && navigate(`/servicios/${svc.id}`)}
                                >
                                    {/* Month Header */}
                                    <div className={`text-center py-2 border-b ${svc ? 'border-inherit' : 'border-slate-100'}`}>
                                        <span className={`text-xs font-bold ${svc ? estadoConfig.textColor : 'text-slate-400'}`}>
                                            {month}
                                        </span>
                                        {isCurrentMonth && (
                                            <span className="ml-1 text-[10px] bg-[#5B9A9A] text-white px-1.5 py-0.5 rounded-full">
                                                Hoy
                                            </span>
                                        )}
                                    </div>
                                    
                                    {/* Content */}
                                    <div className="flex-1 p-2 flex flex-col justify-between">
                                        {svc ? (
                                            <>
                                                {/* Estado */}
                                                <div className="text-center mb-2">
                                                    <div className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${estadoConfig.color} text-white mb-1`}>
                                                        {(() => {
                                                            const IconComponent = estadoConfig.icon;
                                                            return <IconComponent size={16} />;
                                                        })()}
                                                    </div>
                                                    <p className={`text-[10px] font-medium ${estadoConfig.textColor} leading-tight`}>
                                                        {estadoConfig.label}
                                                    </p>
                                                </div>
                                                
                                                {/* EDP Badge (if applicable) */}
                                                {svc.requiere_edp && (
                                                    <div className="text-center mb-2">
                                                        <span className={`inline-flex items-center text-[9px] px-1.5 py-0.5 rounded ${
                                                            edpConfig 
                                                                ? `${edpConfig.color} text-white` 
                                                                : 'bg-gray-200 text-gray-600'
                                                        }`}>
                                                            <FileText size={10} className="mr-0.5" />
                                                            {edpConfig ? edpConfig.label : 'Sin EDP'}
                                                        </span>
                                                    </div>
                                                )}
                                                
                                                {/* Montos */}
                                                <div className="space-y-0.5 mt-auto">
                                                    <div className="flex justify-between text-[9px]">
                                                        <span className="text-slate-500">Total:</span>
                                                        <span className="font-medium text-slate-700">
                                                            {formatCurrency(svc.monto_total)}
                                                        </span>
                                                    </div>
                                                    <div className="flex justify-between text-[9px]">
                                                        <span className="text-slate-500">Pagado:</span>
                                                        <span className="font-medium text-emerald-600">
                                                            {formatCurrency((svc.monto_total || 0) - (svc.saldo_pendiente || svc.monto_total || 0))}
                                                        </span>
                                                    </div>
                                                    <div className="flex justify-between text-[9px]">
                                                        <span className="text-slate-500">Pend:</span>
                                                        <span className={`font-medium ${(svc.saldo_pendiente || 0) > 0 ? 'text-red-600' : 'text-slate-600'}`}>
                                                            {formatCurrency(svc.saldo_pendiente || svc.monto_total)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="flex-1 flex items-center justify-center">
                                                <span className="text-[10px] text-slate-400 text-center">
                                                    {isFuture ? 'Futuro' : 'Sin registro'}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </CardContent>
            </Card>

            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-[#D5E8E8] rounded-lg">
                                <Calendar size={20} className="text-[#5B9A9A]" />
                            </div>
                            <div>
                                <p className="text-xs text-slate-500">Períodos en {selectedYear}</p>
                                <p className="text-xl font-bold text-slate-900">
                                    {serviciosPeriodo.filter(s => s.periodo_anio === selectedYear).length}
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-emerald-100 rounded-lg">
                                <CheckCircle size={20} className="text-emerald-600" />
                            </div>
                            <div>
                                <p className="text-xs text-slate-500">Pagados</p>
                                <p className="text-xl font-bold text-emerald-600">
                                    {serviciosPeriodo.filter(s => s.estado === 'pagado').length}
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-amber-100 rounded-lg">
                                <Clock size={20} className="text-amber-600" />
                            </div>
                            <div>
                                <p className="text-xs text-slate-500">Pendientes</p>
                                <p className="text-xl font-bold text-amber-600">
                                    {serviciosPeriodo.filter(s => !['pagado', 'castigado'].includes(s.estado)).length}
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-slate-100 rounded-lg">
                                <DollarSign size={20} className="text-slate-600" />
                            </div>
                            <div>
                                <p className="text-xs text-slate-500">Total Año</p>
                                <p className="text-xl font-bold text-slate-900">
                                    {formatCurrency(serviciosPeriodo.reduce((sum, s) => sum + (parseFloat(s.monto_total) || 0), 0))}
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Info note for non-recurring services */}
            {!servicio.grupo_recurrente_id && serviciosPeriodo.length <= 1 && (
                <Card className="bg-[#E8F4F4] border-[#A5D0D0]">
                    <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                            <div className="p-1.5 bg-[#D5E8E8] rounded-lg mt-0.5">
                                <FileText size={16} className="text-[#5B9A9A]" />
                            </div>
                            <div>
                                <p className="text-sm font-medium text-[#2D5A5A]">Servicio único</p>
                                <p className="text-xs text-[#5B9A9A] mt-0.5">
                                    Este servicio no es recurrente. Solo se muestra el período para el cual fue creado. 
                                    Los demás meses aparecen vacíos porque no existen servicios con el mismo nombre para esos períodos.
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
};

export default ServicioTimeline;
